import classnames from 'classnames';
import Icon from 'app/components/partials/icon';
import Loader from 'app/components/partials/loader';
import MultiSelect from 'app/components/partials/multi-select';
import PropTypes from 'prop-types';
import React, { useState } from 'react';

export const SUBMIT_SUCCESS_STATUS = 'submittedSuccessfully';
export const SUBMIT_FAILURE_STATUS = 'submittedFailed';

export const getInitialValues = (fields) => {
    const values = {};
    fields.forEach((field) => {
        if (field.type === 'multi-select') {
            values[field.name] = [];
        } else if (field.type === 'checkbox') {
            values[field.name] = false;
        } else if (field.type === 'number') {
            values[field.name] = null;
        } else {
            values[field.name] = '';
        }
    });

    return values;
};

/**
 * Formats the Venues API response into the desired dropdown list shape
 *
 * @param  {object} response      - Response from API
 * @return {array} options   - Array of the options
 */
const formatOptionsResponse = (response) => {
    const { data } = response;
    const options = [];

    // Re-format each response item
    data.forEach(({ attributes }) => {
        const { name, thumbnail } = attributes;

        options.push({
            label: name,
            value: name,
            thumbnail: thumbnail
        });
    });

    return options;
};

export const EnquiryForm = ({ type, formFields, formValues, setFormValues, formErrors, setFormErrors, validateField, responseStatus, handleSubmit, isSubmitting }) => {
    const handleChange = (name, value) => {
        setFormValues((prev) => ({ ...prev, [name]: value }));
    };

    const handleBlur = (field) => {
        const error = validateField(field, formValues[field.name]);
        setFormErrors((prev) => ({ ...prev, [field.name]: error }));
    };

    const renderInput = (field) => {
        if (field.type === 'single-select') {
            return (
                <select
                    id={field.name}
                    className="select"
                    value={formValues[field.name]}
                    onChange={(e) => handleChange(field.name, e.target.value)}
                    onBlur={() => handleBlur(field)}
                >
                    {field.options.map((opt) => {
                        const keyValue = (opt.value === null || opt.value === undefined) ? 'null' : String(opt.value);
                        const optionValue = (opt.value === null || opt.value === undefined) ? '' : opt.value;

                        return (
                            <option key={keyValue} value={optionValue}>{opt.label}</option>
                        );
                    })}
                </select>
            );
        }
        if (field.type === 'multi-select') {
            // Fetch options if it's a function, otherwise use array
            const [options, setOptions] = useState([]);
            React.useEffect(() => {
                if (typeof field.options === 'function') {
                    field.options().then((response) => {
                        setOptions(formatOptionsResponse(response) || []);
                    });
                } else {
                    setOptions(field.options || []);
                }
            }, []);

            const value = formValues[field.name];
            const placeholder = field.placeholder || '';
            const handleToggleValue = (newValue) => {
                const updatedValues = Array.isArray(value) ? [...value] : [];
                const inArrayPosition = updatedValues.indexOf(newValue);

                if (inArrayPosition > -1) {
                    updatedValues.splice(inArrayPosition, 1);
                } else {
                    updatedValues.push(newValue);
                }

                handleChange(field.name, updatedValues);
            };

            return (
                <MultiSelect
                    handleToggleValue={handleToggleValue}
                    className={formErrors[field.name] ? 'is-invalid' : ''}
                    name={field.name}
                    options={options}
                    placeholder={placeholder}
                    values={value}
                    onBlur={() => handleBlur(field)}
                />
            );
        }
        if (field.type === 'checkbox') {
            return (
                <>
                    <input
                        id={field.name}
                        type="checkbox"
                        checked={formValues[field.name]}
                        onChange={(e) => handleChange(field.name, e.target.checked)}
                        onBlur={() => handleBlur(field)}
                    />
                    <span className="checkbox-button" aria-hidden="true">
                        <Icon className="icon" name="tick" />
                    </span>
                </>
            );
        }
        if (field.type === 'multi-line') {
            return (
                <textarea
                    id={field.name}
                    className="textarea"
                    placeholder={field.placeholder}
                    value={formValues[field.name]}
                    onChange={(e) => handleChange(field.name, e.target.value)}
                    onBlur={() => handleBlur(field)}
                />
            );
        }
        if (field.type === 'number') {
            return (
                <input
                    id={field.name}
                    type={field.type}
                    className="input"
                    placeholder={field.placeholder}
                    value={formValues[field.name]}
                    onChange={(e) => handleChange(field.name, Number(e.target.value))}
                    onBlur={() => handleBlur(field)}
                />
            );
        }
        if (field.type === 'date') {
            // allows dynamically setting the min date based on another field value
            const min = field.minField ? formValues[field.minField] : field.min;

            return (
                <input
                    id={field.name}
                    type={field.type}
                    min={min}
                    className="input"
                    placeholder={field.placeholder}
                    value={formValues[field.name]}
                    onChange={(e) => handleChange(field.name, e.target.value)}
                    onBlur={() => handleBlur(field)}
                />
            );
        }

        return (
            <input
                id={field.name}
                type={field.type}
                className="input"
                placeholder={field.placeholder}
                value={formValues[field.name]}
                onChange={(e) => handleChange(field.name, e.target.value)}
                onBlur={() => handleBlur(field)}
            />
        );
    };

    const renderField = (field) => {
        const isHalfWidth = field.width === 'half';
        const isFullWidth = !field.width || field.width === 'full';

        return (
            <div className={classnames('field', { checkbox: field.type === 'checkbox', half: isHalfWidth, full: isFullWidth })} key={field.name}>
                <label className="label" htmlFor={field.name}>
                    <span className="label-static">{field.label}</span>
                    {formErrors[field.name] && (
                        <span className="label-validation">{formErrors[field.name]}</span>
                    )}
                </label>
                {renderInput(field)}
            </div>
        );
    };

    const renderSubmitSection = () => {
        if (responseStatus === SUBMIT_SUCCESS_STATUS) {
            return (
                <div className="form-submission">
                    {type === 'contact-form' && (
                        // No need to show feedback for newsletter signup
                        <p key="validation" className="form-submission-validation is-active">
                            Thanks for getting in touch!
                            <br />
                            A member of our team will be in touch to discuss your requirements as soon as possible.
                        </p>
                    )}
                    <button key="form-button" type="button" className="form-submission-button || button alternative" disabled>
                        <Icon name="tick" /><span>Sent</span>
                    </button>
                </div>
            );
        }

        if (responseStatus === SUBMIT_FAILURE_STATUS) {
            return (
                <div className="form-submission">
                    <p key="validation" className="form-submission-validation is-active error">Sorry, we didn’t get your message. Please try again later.</p>
                    {renderSubmitButton()}
                </div>
            );
        }

        return (
            <div className="form-submission">
                <p key="validation" className="form-submission-validation is-inactive"></p>
                {renderSubmitButton()}
            </div>
        );
    };

    const renderSubmitButton = () => {
        const visibleFields = formFields.filter((field) => !field.showIf || field.showIf(formValues));
        let allFieldsValid = true;
        visibleFields.forEach((field) => {
            if (field.validator) {
                const result = field.validator(formValues[field.name], formValues);
                if (!result.valid) {
                    allFieldsValid = false;
                }
            }
        });
        const valid = allFieldsValid && !isSubmitting;

        return (
            <button
                key="button"
                type="submit"
                className="form-submission-button || button alternative"
                disabled={!valid}
            >
                {isSubmitting ? (
                    <>
                        <Loader type="small primary" />
                        <span>Submitting…</span>
                    </>
                ) : (
                    'Submit'
                )}
            </button>
        );
    };

    return (
        <section className="contact-form constrain-width">
            <form className="form" onSubmit={handleSubmit}>
                {formFields
                    .filter((field) => !field.showIf || field.showIf(formValues))
                    .map((field) => (
                        renderField(field)
                    ))}
                {type === 'contact-form' && (
                    <>
                        <p className="privacy-policy">By ticking this box, I consent to the collection and use of my personal information to receive relevant news, offers, and updates. I understand I can unsubscribe at any time by clicking “Unsubscribe” in any email. I agree to receiving communications from Auckland Conventions Venues & Events.</p>
                        <p className="privacy-policy">
                            By submitting this form, you consent to Auckland Conventions Venues & Events collecting and using your personal information to respond to your enquiry. Your information will not be used for marketing purposes unless you explicitly tick the box above.
                        </p>
                    </>
                )}
                <p className="privacy-policy">To learn more about how we manage and protect your personal information, please review our <a href="/privacy-policy">Privacy Policy</a> and <a href="/terms-and-conditions">Terms of Service</a>.</p>
                <p className="privacy-policy">This site is protected by reCAPTCHA and the Google <a href="https://policies.google.com/privacy">Privacy Policy</a> and <a href="https://policies.google.com/terms">Terms of Service</a> apply.</p>
                {renderSubmitSection()}
            </form>
        </section>
    );
};

EnquiryForm.propTypes = {
    type: PropTypes.string,
    formFields: PropTypes.arrayOf(PropTypes.shape({
        name: PropTypes.string.isRequired,
        label: PropTypes.string,
        type: PropTypes.string.isRequired,
        options: PropTypes.oneOfType([PropTypes.array, PropTypes.func]),
        placeholder: PropTypes.string,
        validator: PropTypes.func,
        showIf: PropTypes.func,
        width: PropTypes.oneOf(['half', 'full'])
    })).isRequired,
    formValues: PropTypes.object.isRequired,
    setFormValues: PropTypes.func.isRequired,
    formErrors: PropTypes.object.isRequired,
    setFormErrors: PropTypes.func.isRequired,
    validateField: PropTypes.func.isRequired,
    responseStatus: PropTypes.string,
    handleSubmit: PropTypes.func.isRequired,
    isSubmitting: PropTypes.bool
};
